import React, { useState } from 'react'
import { motion } from 'framer-motion'
import { useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/lib/auth'
import { X } from 'lucide-react'
import { toast } from 'sonner'
import UserPicker from './UserPicker'

/**
 * Create a new group chat with N>1 other workspace members.
 *
 * Groups have name=NULL; display name is computed from participants at
 * render time (see ChannelSidebar / TeamChat header). Group uniqueness
 * is NOT enforced — multiple groups with the same exact members are
 * permitted (operators might intentionally want a fresh thread).
 *
 * Minimum: 2 other users selected (group requires 3+ participants
 * including the current user). 1-other is a DM, which lives in
 * StartDMModal.
 */

export default function NewGroupModal({
  onClose,
  onGroupCreate,
}: {
  onClose: () => void
  onGroupCreate: (conversationId: string) => void
}) {
  const { profile, workspace } = useAuth()
  const queryClient = useQueryClient()
  const [selectedIds, setSelectedIds] = useState<string[]>([])
  const [busy, setBusy] = useState(false)

  const canCreate = selectedIds.length >= 2 && !busy

  const handleCreate = async () => {
    if (!canCreate || !profile?.id || !workspace?.id) return
    setBusy(true)
    try {
      const { data: created, error: cErr } = await supabase
        .from('team_conversations')
        .insert({
          workspace_id: workspace.id,
          type: 'group',
          name: null,
          created_by: profile.id,
        })
        .select('id')
        .single()
      if (cErr) throw cErr

      const rows = [
        { team_conversation_id: created.id, user_id: profile.id },
        ...selectedIds.map(id => ({ team_conversation_id: created.id, user_id: id })),
      ]
      const { error: pErr } = await supabase
        .from('team_conversation_participants')
        .insert(rows)
      if (pErr) throw pErr

      queryClient.invalidateQueries({ queryKey: ['team-joined-dms-groups'] })
      queryClient.invalidateQueries({ queryKey: ['team-chat-unread'] })
      toast.success('Group created')
      onGroupCreate(created.id as string)
    } catch (e: any) {
      toast.error(e?.message || 'Failed to create group')
      setBusy(false)
    }
  }

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      style={{ position: 'fixed', inset: 0, background: 'var(--overlay-scrim)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100, padding: 24 }}
      onClick={onClose}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 12 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.95 }} transition={{ duration: 0.15 }}
        onClick={e => e.stopPropagation()}
        style={{ background: 'var(--surface)', border: '1px solid var(--border-default)', borderRadius: 20, width: '100%', maxWidth: 480, maxHeight: '85vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
      >
        <div style={{ padding: '20px 24px 16px', borderBottom: '1px solid var(--border-subtle)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <h2 style={{ fontSize: 18, fontWeight: 800, color: 'var(--ink-primary)', margin: 0 }}>New group chat</h2>
            <p style={{ fontSize: 12, color: 'var(--ink-tertiary)', margin: '3px 0 0' }}>Pick two or more teammates. You'll all be added to the group.</p>
          </div>
          <button onClick={onClose} aria-label="Close" style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--ink-tertiary)', padding: 4, display: 'flex' }}>
            <X style={{ width: 18, height: 18 }} />
          </button>
        </div>

        <div style={{ flex: 1, minHeight: 0, padding: '16px 24px', display: 'flex', flexDirection: 'column' }}>
          {workspace?.id && profile?.id && (
            <UserPicker
              workspaceId={workspace.id}
              excludeUserIds={[profile.id]}
              mode="multi"
              selectedIds={selectedIds}
              onChange={setSelectedIds}
              placeholder="Add people to the group…"
            />
          )}
        </div>

        <div style={{ padding: '14px 24px', borderTop: '1px solid var(--border-subtle)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
          <p style={{ fontSize: 11, color: 'var(--ink-tertiary)', margin: 0 }}>
            {selectedIds.length === 0 ? 'Pick at least 2 teammates' :
             selectedIds.length === 1 ? 'Pick at least 1 more' :
             `${selectedIds.length + 1} members total`}
          </p>
          <div style={{ display: 'flex', gap: 10 }}>
            <button onClick={onClose} className="btn-ghost">Cancel</button>
            <button
              onClick={handleCreate}
              disabled={!canCreate}
              className="btn-primary"
            >
              {busy ? 'Creating…' : 'Create group'}
            </button>
          </div>
        </div>
      </motion.div>
    </motion.div>
  )
}
